/**
 * useGooglePlacesAutocomplete
 * Loads the Google Maps JavaScript API once and exposes autocomplete functionality.
 * Uses the Places Autocomplete service (no CORS issues).
 */
import { useState, useEffect, useRef, useCallback } from "react";

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LOCATION_ANCHOR_KEY = "ogestyle_location_anchor_v1";
const BROWSER_LOCATION_TTL_MS = 24 * 60 * 60 * 1000;
let mapsLoadPromise = null;

const extractCityStateLabel = (addressComponents) => {
  if (!Array.isArray(addressComponents)) return null;

  let city = "";
  let state = "";

  addressComponents.forEach((component) => {
    const types = component?.types || [];

    if (!city && (types.includes("locality") || types.includes("postal_town"))) {
      city = component.long_name || "";
    }

    if (!city && types.includes("administrative_area_level_2")) {
      city = component.long_name || "";
    }

    if (!state && types.includes("administrative_area_level_1")) {
      state = component.short_name || component.long_name || "";
    }
  });

  if (!city && !state) return null;
  if (!city) return state || null;
  if (!state) return city;
  return `${city}, ${state}`;
};

const readStoredAnchor = () => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(LOCATION_ANCHOR_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lng = Number(parsed?.lng);
    const timestamp = Number(parsed?.timestamp);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const source = parsed?.source === "manual" ? "manual" : parsed?.source === "browser" ? "browser" : null;
    if (!source) return null;

    if (
      source === "browser" &&
      (!Number.isFinite(timestamp) || Date.now() - timestamp > BROWSER_LOCATION_TTL_MS)
    ) {
      clearStoredAnchor();
      return null;
    }

    return {
      lat,
      lng,
      label: parsed?.label || "Selected location",
      source,
    };
  } catch {
    return null;
  }
};

const writeStoredAnchor = (anchor) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      LOCATION_ANCHOR_KEY,
      JSON.stringify({
        ...anchor,
        timestamp: Date.now(),
      }),
    );
  } catch {
    // Ignore storage failures
  }
};

const clearStoredAnchor = () => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(LOCATION_ANCHOR_KEY);
  } catch {
    // Ignore storage failures
  }
};

function loadGoogleMapsScript() {
  if (window.google?.maps?.places) return Promise.resolve();
  if (mapsLoadPromise) return mapsLoadPromise;
  mapsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return mapsLoadPromise;
}

export function useGooglePlacesAutocomplete() {
  const initialStoredAnchor = readStoredAnchor();
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [browserCoords, setBrowserCoords] = useState(null);
  const [selectedCoords, setSelectedCoords] = useState(
    initialStoredAnchor ? {
      lat: initialStoredAnchor.lat,
      lng: initialStoredAnchor.lng,
      label: initialStoredAnchor.label,
    } : null,
  ); // { lat, lng, label }
  const [locationSource, setLocationSource] = useState(
    initialStoredAnchor?.source || null,
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [geolocationStatus, setGeolocationStatus] = useState(() => {
    if (typeof window === "undefined") return "idle";
    return window.navigator?.geolocation ? "requesting" : "unavailable";
  });
  const [mapsReady, setMapsReady] = useState(!!window.google?.maps?.places);
  const autocompleteServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const debounceRef = useRef(null);
  const geolocationRequestedRef = useRef(false);
  const inputValueRef = useRef("");
  const locationSourceRef = useRef(null);
  const resolvedBrowserLabelRef = useRef(null);

  useEffect(() => {
    if (!MAPS_API_KEY) return;
    loadGoogleMapsScript()
      .then(() => {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        geocoderRef.current = new window.google.maps.Geocoder();
        setMapsReady(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    inputValueRef.current = inputValue;
    locationSourceRef.current = locationSource;
  }, [inputValue, locationSource]);

  useEffect(() => {
    if (initialStoredAnchor?.source === "manual") {
      setInputValue(initialStoredAnchor.label || "");
    } else if (initialStoredAnchor?.source === "browser") {
      setBrowserCoords({
        lat: initialStoredAnchor.lat,
        lng: initialStoredAnchor.lng,
        label: initialStoredAnchor.label,
      });
    } else if (!initialStoredAnchor) {
      clearStoredAnchor();
    }
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyBrowserCoords = useCallback((coords) => {
    setBrowserCoords(coords);
    writeStoredAnchor({ ...coords, source: "browser" });

    if (!inputValueRef.current.trim() && locationSourceRef.current !== "manual") {
      setSelectedCoords(coords);
      setLocationSource("browser");
    }
  }, []);

  useEffect(() => {
    if (!mapsReady || !geocoderRef.current || !browserCoords) return;

    const resolutionKey = `${browserCoords.lat}:${browserCoords.lng}`;
    if (resolvedBrowserLabelRef.current === resolutionKey) return;

    const currentLabel = (browserCoords.label || "").trim();
    if (currentLabel && currentLabel !== "Current location" && currentLabel !== "Selected location") {
      resolvedBrowserLabelRef.current = resolutionKey;
      return;
    }

    geocoderRef.current.geocode(
      { location: { lat: browserCoords.lat, lng: browserCoords.lng } },
      (results, status) => {
        if (status !== "OK" || !results?.[0]) return;

        const resolvedLabel = extractCityStateLabel(results[0].address_components);
        if (!resolvedLabel) return;

        resolvedBrowserLabelRef.current = resolutionKey;
        const nextCoords = {
          lat: browserCoords.lat,
          lng: browserCoords.lng,
          label: resolvedLabel,
        };

        setBrowserCoords(nextCoords);
        writeStoredAnchor({ ...nextCoords, source: "browser" });

        if (locationSourceRef.current === "browser") {
          setSelectedCoords(nextCoords);
        }
      },
    );
  }, [browserCoords, mapsReady]);

  useEffect(() => {
    if (geolocationRequestedRef.current) return;
    geolocationRequestedRef.current = true;

    if (!window.navigator?.geolocation) {
      setGeolocationStatus("unavailable");
      return;
    }

    window.navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeolocationStatus("granted");
        applyBrowserCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          label: "Current location",
        });
      },
      (error) => {
        setGeolocationStatus(error?.code === 1 ? "denied" : "error");

        if (!inputValueRef.current.trim() && !locationSourceRef.current) {
          setSelectedCoords(null);
          setLocationSource(null);
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000,
      },
    );
  }, [applyBrowserCoords]);

  const fetchSuggestions = useCallback(
    (query) => {
      if (!mapsReady || !autocompleteServiceRef.current || !query || query.length < 2) {
        setSuggestions([]);
        return;
      }
      autocompleteServiceRef.current.getPlacePredictions(
        { input: query, types: ["(cities)"] },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        },
      );
    },
    [mapsReady],
  );

  const handleInputChange = useCallback(
    (val) => {
      setInputValue(val);
      if (!val) {
        setSelectedCoords(browserCoords);
        setSuggestions([]);
        setShowSuggestions(false);
        setLocationSource(browserCoords ? "browser" : null);
        return;
      }
      setSelectedCoords(null);
      setLocationSource(null);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(val);
        setShowSuggestions(true);
      }, 300);
    },
    [browserCoords, fetchSuggestions],
  );

  const selectSuggestion = useCallback(
    (prediction) => {
      setShowSuggestions(false);
      setSuggestions([]);
      setInputValue(prediction.description);
      if (!geocoderRef.current) return;
      geocoderRef.current.geocode({ placeId: prediction.place_id }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          const loc = results[0].geometry.location;
          setLocationSource("manual");
          const manualCoords = {
            lat: loc.lat(),
            lng: loc.lng(),
            label: prediction.description,
          };

          setSelectedCoords(manualCoords);
          writeStoredAnchor({ ...manualCoords, source: "manual" });
        }
      });
    },
    [],
  );

  const clearLocation = useCallback(() => {
    setInputValue("");
    setSelectedCoords(browserCoords);
    setLocationSource(browserCoords ? "browser" : null);
    setSuggestions([]);
    setShowSuggestions(false);

    clearStoredAnchor();
    if (browserCoords) {
      writeStoredAnchor({ ...browserCoords, source: "browser" });
    }
  }, [browserCoords]);

  return {
    inputValue,
    suggestions,
    selectedCoords,
    locationSource,
    geolocationStatus,
    showSuggestions,
    mapsReady,
    handleInputChange,
    selectSuggestion,
    clearLocation,
    setShowSuggestions,
  };
}
